'use client';

import { cn } from '@fshr/shared-react';
import IconCard from './icon-card';
import { formatPrice } from '@fshr/shared';
import Modal from '../modal';
import type { IconType } from '../icon/icon';
import { IconCardProps } from './types';
import { ExperiencePricesModalBody } from './price-modal';
import type { PricingList } from '@micro-apps/stores';
import { useState } from 'react';
import { useLocale, useTranslations } from '@fshr/context/i18n-context';

type IconCardRowProps = {
	price?: number;
	complimentary?: boolean;
	duration?: string;
	availability?: string;
	timeOfDay?: string;
	isPriceLoading: boolean;
	areProductDetailsLoading: boolean;
	pricePending?: boolean;
	// Absent on the server, where there is no `userStore`; `formatPrice` falls back to USD.
	currencyCode?: string;
	pricingList?: PricingList;
	pricingListDisclaimer?: string;
};

const logisticsBlockTypeIconTypeMap: Record<string, IconType> = {
	price: 'offers',
	duration: 'time',
	availability: 'itinerary_default',
	timeOfDay: 'sunny_cloudy',
};

export default function IconCardRow({
	price,
	complimentary = false,
	duration,
	availability,
	timeOfDay,
	isPriceLoading: isPriceLoadingProp,
	areProductDetailsLoading,
	pricePending = false,
	currencyCode,
	pricingList,
	pricingListDisclaimer,
}: IconCardRowProps) {
	const [isPricingModalOpen, setPricingModalOpen] = useState(false);
	const t = useTranslations();
	const locale = useLocale();

	const blocks: (IconCardProps & { type: 'price' | 'duration' | 'availability' | 'timeOfDay' })[] =
		[];

	const hasPrice = price !== undefined && price !== null;

	const priceIsLoading = isPriceLoadingProp || (!hasPrice && !complimentary);

	if (hasPrice || complimentary || pricePending || areProductDetailsLoading) {
		// Interpolated through `t` so the "From" prefix is translatable — `t('From
		// $1,911')` would never resolve against the dictionary.
		const priceData = complimentary
			? t('Complimentary')
			: hasPrice
				? t('From {0}', [formatPrice({ value: price, currencyCode, locale, decimalPlaces: 0 })])
				: '';

		blocks.push({
			type: 'price',
			label: 'Price',
			data: priceData,
			icon: logisticsBlockTypeIconTypeMap['price'],
			isLoading: areProductDetailsLoading,
			isDataLoading: priceIsLoading,
			cta: pricingList?.length
				? {
						label: 'Show All Prices',
						onClick: () => {
							setPricingModalOpen(true);
						},
					}
				: undefined,
		});
	}
	if (duration) {
		blocks.push({
			type: 'duration',
			label: 'Duration',
			data: duration,
			icon: logisticsBlockTypeIconTypeMap['duration'],
			isLoading: areProductDetailsLoading,
		});
	}
	if (availability) {
		blocks.push({
			type: 'availability',
			label: 'Availability',
			data: availability,
			icon: logisticsBlockTypeIconTypeMap['availability'],
			isLoading: areProductDetailsLoading,
		});
	}
	if (timeOfDay) {
		blocks.push({
			type: 'timeOfDay',
			label: 'Time of Day',
			data: timeOfDay,
			icon: logisticsBlockTypeIconTypeMap['timeOfDay'],
			isLoading: areProductDetailsLoading,
		});
	}

	if (blocks.length === 0) return null;

	const gridCols = blocks.length === 1 ? 'grid-cols-1' : 'grid-cols-2';

	return (
		<>
			<div
				className={cn(
					'grid w-full max-w-[615px]',
					gridCols,
					'auto-rows-fr',
					'spacing-stack-s spacing-inline-s',
					// AC 2.a.ii: with 3 blocks the third spans both columns. The selector
					// counts element children, so the modal must stay outside this grid or
					// opening it would silently drop the span.
					'[&>*:last-child:nth-child(3)]:col-span-2'
				)}
			>
				{blocks.map(block => (
					<IconCard
						key={block.type}
						icon={block.icon}
						label={block.label}
						data={block.data}
						cta={block.cta}
						isLoading={block.isLoading}
						isDataLoading={block.isDataLoading}
					/>
				))}
			</div>
			{isPricingModalOpen && (
				<Modal
					close={() => {
						setPricingModalOpen(false);
					}}
					children={
						<ExperiencePricesModalBody
							pricingList={pricingList ?? []}
							pricingListDisclaimer={pricingListDisclaimer}
						/>
					}
					// Figma "policy modal" paddingBottom: card-padding-l on mobile (40px),
					// card-padding-m on desktop (50px).
					contentContainerClassNames="justify-start pb-card-padding-l md:pb-card-padding-m"
					mobileFullScreen={false}
				/>
			)}
		</>
	);
}
